import { XMarkIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Button, Flex, Image, Text } from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { useRef } from 'react';

import { addBookImage } from '@/entities/books/api/books-api';
import { Book, BookImageType } from '@/entities/books/model/types';

import styles from './book-image.module.scss';

type Props = {
  bookInfo: Book;
  setBookInfo: React.Dispatch<React.SetStateAction<Book>>;
};

export const BookImage = ({ bookInfo, setBookInfo }: Props) => {
  const mainImageDropzoneRef = useRef<() => void>(null);
  const circleImageDropzoneRef = useRef<() => void>(null);
  const [visible] = useDisclosure(false);

  const uploadImage = async (
    file: File,
    imageType: BookImageType,
  ): Promise<{ imageUrl: string; imagePath: string }> => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const data = await addBookImage(formData);

      if (imageType === BookImageType.MAIN) {
        setBookInfo((prevState) => ({
          ...prevState,
          imgUrl: data.imageUrl,
          imagePath: data.imagePath,
        }));
      } else {
        setBookInfo((prevState) => ({
          ...prevState,
          circleImgUrl: data.imageUrl,
          circleImagePath: data.imagePath,
        }));
      }

      if (!data) {
        throw new Error('Ошибка при загрузке изображения');
      }

      return {
        imageUrl: data.imageUrl,
        imagePath: data.imagePath,
      };
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      return { imageUrl: '', imagePath: '' };
    }
  };

  const handleDrop = async (files: File[], imageType: BookImageType) => {
    const file = files[0];

    if (file) {
      if (imageType === BookImageType.MAIN) {
        const uploadedImage = await uploadImage(file, imageType);
        setBookInfo((prev) => ({
          ...prev,
          imgUrl: uploadedImage.imageUrl,
          imagePath: uploadedImage.imagePath,
        }));
      } else {
        const uploadedImage = await uploadImage(file, imageType);
        setBookInfo((prev) => ({
          ...prev,
          circleImgUrl: uploadedImage.imageUrl,
          circleImagePath: uploadedImage.imagePath,
        }));
      }
    }
  };

  const clearImage = (type: string) => {
    setBookInfo((prevState) => ({
      ...prevState,
      [type]: '',
    }));
  };

  return (
    <Flex direction="column" mt={16}>
      <Flex direction="row" gap={32} align="center">
        <Dropzone
          onDrop={(files) => handleDrop(files, BookImageType.MAIN)}
          accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.svg]}
          loading={visible}
          openRef={mainImageDropzoneRef}
          maxSize={400 * 1024}
        >
          <Flex direction="column" align="center" gap={12}>
            <Text span color="#8E8E8E">
              Перетащите файл или выберите
            </Text>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                mainImageDropzoneRef.current?.();
              }}
            >
              Выбрать квадратную обложку
            </Button>
          </Flex>
        </Dropzone>
        <Dropzone
          openRef={circleImageDropzoneRef}
          accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.svg]}
          onDrop={(files) => handleDrop(files, BookImageType.CIRCLE)}
          maxSize={400 * 1024}
        >
          <Flex direction="column" align="center" gap={12}>
            <Text span color="#8E8E8E">
              Перетащите файл или выберите
            </Text>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                circleImageDropzoneRef.current?.();
              }}
            >
              Выбрать круглую обложку
            </Button>
          </Flex>
        </Dropzone>
      </Flex>
      <Flex direction="column" mb={20} mt={8}>
        <Text span color="#8E8E8E">
          Допустимые форматы файлов: png, jpeg, svg
        </Text>
        <Text span color="#8E8E8E">
          Размер файла не должен превышать 400 кб
        </Text>
      </Flex>

      <Flex direction="row" gap={50} align="center">
        {bookInfo.imgUrl && (
          <Flex
            direction="column"
            gap={8}
            align="center"
            w={120}
            style={{ position: 'relative' }}
          >
            <ActionIcon
              variant="outline"
              onClick={() => clearImage('imgUrl')}
              className={styles['clear-icon']}
            >
              <XMarkIcon width={20} height={20} />
            </ActionIcon>
            <Image
              src={bookInfo.imgUrl}
              alt="Основное изображение"
              width={120}
              height={120}
              style={{ objectFit: 'cover', borderRadius: '8px' }}
            />
          </Flex>
        )}

        {bookInfo.circleImgUrl && (
          <Flex
            direction="column"
            gap={8}
            align="center"
            w={120}
            style={{ position: 'relative' }}
          >
            <ActionIcon
              variant="outline"
              onClick={() => clearImage('circleImgUrl')}
              className={styles['clear-icon']}
            >
              <XMarkIcon width={20} height={20} />
            </ActionIcon>
            <Image
              src={bookInfo.circleImgUrl}
              alt="Круглое изображение"
              width={120}
              height={120}
              style={{ objectFit: 'cover', borderRadius: '50%' }}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
